import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(() => localStorage.getItem('co_regulate_token'));
    const [loading, setLoading] = useState(true);
    const [subscription, setSubscription] = useState(null);

    // Create a stable axios instance that updates when token changes
    const api = useMemo(() => {
        return () => axios.create({
            baseURL: API,
            headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
    }, [token]);

    // Fetch subscription data
    const fetchSubscription = useCallback(async (authToken) => {
        try {
            const response = await axios.get(`${API}/users/me/subscription`, {
                headers: { Authorization: `Bearer ${authToken}` }
            });
            setSubscription(response.data);
        } catch (error) {
            console.error('Failed to fetch subscription:', error);
            // Default to free tier with all features locked
            setSubscription({
                tier: 'free',
                features: ['All overwhelm types', 'Core grounding steps', 'Basic regulation tools'],
                // Support tier features
                can_save_favourites: false,
                can_access_extended_library: false,
                can_access_light_journaling: false,
                can_enable_reminders: false,
                can_access_early_overwhelm_types: false,
                can_access_micro_tools: false,
                // Deep Support tier features
                can_access_coregulation: false,
                can_start_shared_session: false,
                can_access_full_communication: false,
                can_access_full_journaling: false,
                can_access_relationship_templates: false
            });
        }
    }, []);

    // Verify token on mount only
    useEffect(() => {
        const verifyToken = async () => {
            const storedToken = localStorage.getItem('co_regulate_token');
            if (storedToken) {
                try {
                    const response = await axios.get(`${API}/auth/me`, {
                        headers: { Authorization: `Bearer ${storedToken}` }
                    });
                    setUser(response.data);
                    await fetchSubscription(storedToken);
                } catch (error) {
                    console.error('Token verification failed:', error);
                    localStorage.removeItem('co_regulate_token');
                    setToken(null);
                    setUser(null);
                    setSubscription(null);
                }
            }
            setLoading(false);
        };
        verifyToken();
    }, [fetchSubscription]); // Only run once on mount

    const login = useCallback(async (email, password) => {
        // Retry logic for network issues
        const maxRetries = 2;
        let lastError = null;
        
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
                const response = await axios.post(`${API}/auth/login`, { email, password }, {
                    timeout: 30000, // 30 second timeout
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    // Disable withCredentials to avoid CORS issues with wildcards
                    withCredentials: false
                });
                const { access_token, user: userData } = response.data;
                localStorage.setItem('co_regulate_token', access_token);
                setToken(access_token);
                setUser(userData);
                await fetchSubscription(access_token);
                return userData;
            } catch (error) {
                lastError = error;
                console.error(`Login attempt ${attempt + 1} failed:`, error.code, error.message);
                
                // Only retry on network errors, not on auth failures
                if (error.response?.status === 401 || error.response?.status === 400) {
                    throw error; // Don't retry auth errors
                }
                
                // Wait before retrying (exponential backoff)
                if (attempt < maxRetries) {
                    await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
                }
            }
        }
        
        // All retries failed
        throw lastError;
    }, [fetchSubscription]);

    const register = useCallback(async (name, email, password, disclaimerAccepted = false) => {
        const response = await axios.post(`${API}/auth/register`, { 
            name, 
            email, 
            password,
            disclaimer_accepted: disclaimerAccepted,
            disclaimer_accepted_at: disclaimerAccepted ? new Date().toISOString() : null
        });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('co_regulate_token', access_token);
        setToken(access_token);
        setUser(userData);
        await fetchSubscription(access_token);
        return userData;
    }, [fetchSubscription]);

    const logout = useCallback(() => {
        localStorage.removeItem('co_regulate_token');
        setToken(null);
        setUser(null);
        setSubscription(null);
    }, []);

    // Helper function to check feature access - maps to paywall triggers
    const canAccess = useCallback((feature) => {
        if (!subscription) return false;
        
        // SUPPORT TIER TRIGGERS
        switch (feature) {
            case 'save_favourites':
                return subscription.can_save_favourites;
            case 'extended_library':
                return subscription.can_access_extended_library;
            case 'light_journaling':
                return subscription.can_access_light_journaling;
            case 'reminders':
                return subscription.can_enable_reminders;
            case 'early_overwhelm_types':
                return subscription.can_access_early_overwhelm_types;
            case 'micro_tools':
                return subscription.can_access_micro_tools;
            
            // DEEP SUPPORT TIER TRIGGERS
            case 'coregulation':
                return subscription.can_access_coregulation;
            case 'shared_session':
                return subscription.can_start_shared_session;
            case 'full_communication':
                return subscription.can_access_full_communication;
            case 'full_journaling':
                return subscription.can_access_full_journaling;
            case 'relationship_templates':
                return subscription.can_access_relationship_templates;
            
            default:
                return true; // Free features are always accessible
        }
    }, [subscription]);

    // Get required tier for a feature (for paywall display)
    const getRequiredTier = useCallback((feature) => {
        const supportFeatures = ['save_favourites', 'extended_library', 'light_journaling', 'reminders', 'early_overwhelm_types', 'micro_tools'];
        const deepSupportFeatures = ['coregulation', 'shared_session', 'full_communication', 'full_journaling', 'relationship_templates'];
        
        if (supportFeatures.includes(feature)) return 'support';
        if (deepSupportFeatures.includes(feature)) return 'deep_support';
        return 'free';
    }, []);

    const value = useMemo(() => ({
        user,
        token,
        loading,
        login,
        register,
        logout,
        api,
        subscription,
        canAccess,
        getRequiredTier,
        isAuthenticated: !!user
    }), [user, token, loading, login, register, logout, api, subscription, canAccess, getRequiredTier]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
